import { useQuery } from '@tanstack/react-query';
import { Search, SearchX } from 'lucide-react';
import WidgetShell from '@/components/admin/dashboard/WidgetShell';
import StatCard from '@/components/admin/dashboard/StatCard';
import api from '@/lib/api';

/**
 * Mirrors the `/admin/search-analytics` handler
 * (functions/src/routes/admin/search-analytics.ts → GET /). NOTE the popular
 * terms carry `search_count` (from COUNT(*)) — NOT `count` — and COUNT/AVG
 * columns serialize as strings from postgres.js.
 */
interface PopularTerm {
  query: string;
  search_count: number | string;
  avg_results: number | string;
}

interface SearchAnalyticsResponse {
  popular_terms: PopularTerm[];
  zero_result_terms: Array<{ query: string; search_count: number | string }>;
  total_searches: number;
  unique_queries: number;
  avg_results_per_search: number | string;
  zero_result_count: number;
  search_volume: unknown[];
}

export default function SearchAnalyticsWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'search', 'analytics', '30'],
    queryFn: () => api.get<SearchAnalyticsResponse>('/admin/search-analytics?days=30'),
    staleTime: 60000,
  });

  return (
    <WidgetShell
      title="Search"
      icon={<Search />}
      to="/admin/search-analytics"
      isLoading={isLoading}
      error={error}
      isEmpty={!!data && data.total_searches === 0}
      emptyIcon={<Search />}
      emptyMessage="No searches in the last 30 days."
      skeletonClassName="h-40"
    >
      {data && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <StatCard
              title="Total searches"
              value={data.total_searches.toLocaleString()}
              icon={<Search />}
              tone="blue"
              subtitle={`${data.unique_queries.toLocaleString()} unique`}
            />
            <StatCard
              title="Zero-result"
              value={data.zero_result_count.toLocaleString()}
              icon={<SearchX />}
              tone="amber"
              subtitle="No matches"
            />
          </div>
          {data.popular_terms.length > 0 && (
            <div>
              <p className="mb-1 px-1 text-xs font-medium text-stone-400">Top searches</p>
              <ul className="divide-y divide-line">
                {data.popular_terms.slice(0, 3).map((term) => (
                  <li
                    key={term.query}
                    className="flex items-center justify-between gap-3 py-1.5"
                  >
                    <span className="truncate text-sm text-ink">{term.query}</span>
                    <span className="shrink-0 text-xs font-medium text-stone-500">
                      {Number(term.search_count).toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </WidgetShell>
  );
}
